import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';

export type PayflowHostedMessage = {
  type: 'payflow-hosted';
  sessionId: string;
  status: 'approved' | 'declined' | 'canceled' | 'error' | string;
  result?: string;
  respmsg?: string;
};

export function payflowDeclineMessage(
  respmsg?: string | null,
  result?: string | null
): string {
  const msg = (respmsg ?? '').trim();
  const code = (result ?? '').trim();
  const usefulMsg = msg && !/^approved$/i.test(msg) ? msg : '';
  const usefulCode = code && code !== '0' ? code : '';
  if (usefulMsg && usefulCode && usefulMsg !== usefulCode) {
    return `Card was not approved. ${usefulMsg} (${usefulCode})`;
  }
  if (usefulMsg) {
    return `Card was not approved. ${usefulMsg}`;
  }
  if (usefulCode) {
    return `Card was not approved. PayPal result ${usefulCode}`;
  }
  return 'Card was not approved. You can try again.';
}

type PayflowHostedCardFrameProps = {
  iframeUrl: string;
  sessionId: string;
  height?: number;
  onMessage: (msg: PayflowHostedMessage) => void;
};

const payflowPostTarget = (iframeUrl: string) => {
  const parsed = new URL(iframeUrl);
  const fields: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    fields[key] = value;
  });
  return { action: parsed.origin, fields };
};

/**
 * Embeds Payflow Layout C hosted checkout and listens for return-page postMessage.
 * Payflow expects SECURETOKEN / SECURETOKENID as an HTTP form POST, not a GET query string.
 */
const PayflowHostedCardFrame: React.FC<PayflowHostedCardFrameProps> = ({
  iframeUrl,
  sessionId,
  height = 565,
  onMessage,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const iframeName = `payflow-hosted-${sessionId}`;
  const { action, fields } = useMemo(
    () => payflowPostTarget(iframeUrl),
    [iframeUrl]
  );

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data as PayflowHostedMessage | null;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'payflow-hosted') return;
      if (data.sessionId && data.sessionId !== sessionId) return;
      onMessage(data);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sessionId, onMessage]);

  useEffect(() => {
    formRef.current?.submit();
  }, [iframeUrl, action]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Enter the card details in the secure PayPal form below. Card numbers are
        not sent to Hub. If PayPal shows a receipt, do not enter the card again.
      </Typography>
      <Box
        component="iframe"
        name={iframeName}
        title="PayPal hosted card entry"
        sx={{
          width: '100%',
          height,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
        sandbox="allow-top-navigation allow-scripts allow-same-origin allow-forms allow-modals"
      />
      <Box
        component="form"
        ref={formRef}
        action={action}
        method="POST"
        target={iframeName}
        sx={{ display: 'none' }}
      >
        {Object.entries(fields).map(([name, value]) => (
          <input key={name} type="hidden" name={name} value={value} />
        ))}
      </Box>
    </Box>
  );
};

export default PayflowHostedCardFrame;
